import { Modal } from 'antd';
import type { ModalFuncProps } from 'antd';
import { useCallback } from 'react';
import { useTheme } from 'styled-components';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { ButtonProps } from 'antd/es/button/button';
import { ConfirmModal } from '@/components';
import { DELETE_ACTION_COLOR } from '@/constants/colors';

const { ConfirmModalTitle } = ConfirmModal;
const { confirm } = Modal;

export type UseConfirmModalPayload = ModalFuncProps & {
  /** When true, modal title and secondary (delete) action use destructive red styling. */
  destructive?: boolean;
};

type UseConfirmModal = (payload: UseConfirmModalPayload) => {
  handleConfirm: () => void;
};

const buttonProps: ButtonProps = { size: 'large', style: { fontFamily: 'Inter' } };

const neutralCancelButtonProps: ButtonProps = {
  type: 'text',
  size: 'large',
  style: { fontFamily: 'Inter' }
};

const destructiveCancelButtonProps: ButtonProps = {
  type: 'text',
  danger: true,
  size: 'large',
  style: { fontFamily: 'Inter', color: DELETE_ACTION_COLOR }
};

export const useConfirmModal: UseConfirmModal = ({
  title,
  content,
  onCancel,
  destructive = false,
  ...rest
}) => {
  const { color } = useTheme();

  const handleConfirm = useCallback(
    () =>
      confirm({
        title: destructive ? (
          <ConfirmModalTitle style={{ color: DELETE_ACTION_COLOR }}>{title}</ConfirmModalTitle>
        ) : (
          <ConfirmModalTitle>{title}</ConfirmModalTitle>
        ),
        icon: <ExclamationCircleOutlined size={28} style={{ color: color.danger }} />,
        content,
        cancelButtonProps: destructive ? destructiveCancelButtonProps : neutralCancelButtonProps,
        okButtonProps: buttonProps,
        onCancel,
        /* A destructive modal deletes from its secondary button, which antd
           wires to onCancel — and Esc calls onCancel too, so pressing it to
           back out deleted the record. Mask and close icon are already off. */
        keyboard: !destructive,
        ...rest
      }),
    [color.danger, title, content, rest, onCancel, destructive]
  );

  return { handleConfirm };
};
